import React, { useMemo } from 'react'
import { type UseFormReturn, Controller } from 'react-hook-form'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { H2, Line, Button, Flex } from '../../lib/components'
import type { AdminOpportunityQuestion } from '../../services/opportunity-questions-service'
import { CircularProgress } from '@mui/material'
import { getTimestampWithTimezone } from '../../lib/utils/helpers'
import '../faq-form/faq-form.scss'

export type OpportunityQuestionReplyFormValues = {
  answer: string
}

function isAnswerEmpty(html: string | undefined | null): boolean {
  if (html === null || html === '') {
    return true
  }
  const text = new DOMParser().parseFromString(html as string, 'text/html').body.textContent ?? ''
  return text.trim() === ''
}

export type OpportunityQuestionReplyPresenterProps = {
  question: AdminOpportunityQuestion | null
  form: UseFormReturn<OpportunityQuestionReplyFormValues>
  isLoading: boolean
  isSubmitting: boolean
  onSubmit: (data: OpportunityQuestionReplyFormValues) => Promise<void>
  onCancel: () => void
}

export function OpportunityQuestionReplyPresenter({
  question,
  form,
  isLoading,
  isSubmitting,
  onSubmit,
  onCancel,
}: OpportunityQuestionReplyPresenterProps) {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = form

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['blockquote', 'link'],
          ['clean'],
        ],
      },
      clipboard: { matchVisual: false },
    }),
    [],
  )

  const quillFormats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'blockquote',
    'link',
  ]

  const heading =
    question?.status === 'answered' ? 'Edit opportunity FAQ answer' : 'Answer opportunity FAQ'

  return (
    <>
      {isLoading ? (
        <div className='loading-overlay'>
          <CircularProgress size={40} color='primary' className='loading-overlay__spinner' />
        </div>
      ) : null}
      <Flex variant='column-center'>
        <H2 classes={['mb-24']} text={heading} />
        {question ? (
          <form className='faq-admin-form' onSubmit={handleSubmit(onSubmit)} noValidate>
            <Line />
            <div className='faq-admin-form__field'>
              <span className='faq-admin-form__label'>Opportunity</span>
              <p className='faq-admin-form__hint'>
                {question.opportunity_title || `Opportunity #${question.opportunity_id}`}
              </p>
            </div>
            <div className='faq-admin-form__field'>
              <span className='faq-admin-form__label'>Client</span>
              <p className='faq-admin-form__hint'>
                {[question.asker_name, question.asker_email].filter(Boolean).join(' · ') || '—'}
              </p>
            </div>
            <div className='faq-admin-form__field'>
              <span className='faq-admin-form__label'>Question</span>
              <p className='faq-admin-form__hint'>{question.question_text}</p>
            </div>
            <div className='faq-admin-form__field'>
              <span className='faq-admin-form__label'>Asked on</span>
              <p className='faq-admin-form__hint'>
                {getTimestampWithTimezone(
                  new Date(question.created_at),
                  'Do MMM YYYY HH:mm:ss zzz',
                )}
              </p>
            </div>
            <div className='faq-admin-form__field'>
              <label className='faq-admin-form__label' htmlFor='opportunity-faq-answer'>
                Your answer <span className='faq-admin-form__required'>*</span>
              </label>
              <p className='faq-admin-form__hint'>
                Use the toolbar for bold, italic, lists, quotes, and links. Answers are saved as
                HTML for display on the site.
              </p>
              <div className='faq-admin-form__editor-wrapper' id='opportunity-faq-answer'>
                <Controller
                  name='answer'
                  control={control}
                  rules={{
                    validate: (v) => !isAnswerEmpty(v) || 'Answer is required',
                  }}
                  render={({ field }) => (
                    <ReactQuill
                      theme='snow'
                      value={field.value ?? ''}
                      onChange={(value) => {
                        field.onChange(value)
                      }}
                      onBlur={field.onBlur}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder='Write the answer…'
                    />
                  )}
                />
              </div>
              {errors.answer ? (
                <p className='faq-admin-form__error'>{errors.answer.message}</p>
              ) : null}
            </div>
            <div className='faq-admin-form__actions'>
              <Button
                text={isSubmitting ? 'Saving…' : 'Save answer'}
                variant='basic-btn'
                buttonType='submit'
                isDisabled={isSubmitting}
                dataTestId='opportunity-faq-reply-submit'
                handleClick={() => {}}
              />
              <Button
                text='Cancel'
                variant='secondary-btn'
                buttonType='button'
                handleClick={onCancel}
                isDisabled={isSubmitting}
              />
            </div>
          </form>
        ) : null}
      </Flex>
    </>
  )
}
